import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import {
  expendituresActions,
  expendituresSelectors,
  expendituresThunks,
} from '@crm/features/expenditures/expendituresSlice';
import { crmApi, errMsg } from '@crm/api';
import { hasCrmPermission } from '@crm/permissions';
import { UiButton, UiDateRangePicker, UiInput } from '@shared/common/ui';

function fmtAmount(v) {
  return new Intl.NumberFormat('ru-RU').format(v ?? 0) + ' ₽';
}

function dateToInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function today() {
  return dateToInputValue(new Date());
}

function currentMonthStart() {
  const d = new Date();
  return dateToInputValue(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function ExpendituresPage() {
  const { branchId } = useParams();
  const dispatch = useDispatch();

  const list = useSelector(expendituresSelectors.selectList(branchId));
  const listStatus = useSelector(expendituresSelectors.selectListStatus(branchId));
  const saveStatus = useSelector(expendituresSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(expendituresSelectors.selectSaveError(branchId));
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const canCreate = hasCrmPermission(profile, branches, branchId, 'expenditures.create');
  const canEdit = hasCrmPermission(profile, branches, branchId, 'expenditures.edit');
  const canDelete = hasCrmPermission(profile, branches, branchId, 'expenditures.delete');
  const canViewCoachSalaries = hasCrmPermission(
    profile,
    branches,
    branchId,
    'expenditures.coach_salaries.view',
  );
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('expenses');
  const [salaryRows, setSalaryRows] = useState([]);
  const [salaryTotal, setSalaryTotal] = useState({ actual: 0, planned: 0 });
  const [salaryStatus, setSalaryStatus] = useState('idle');
  const [salaryError, setSalaryError] = useState(null);
  const [from, setFrom] = useState(currentMonthStart());
  const [to, setTo] = useState(today());

  const listParams = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
    }),
    [from, to],
  );

  const reloadList = () => {
    if (branchId) dispatch(expendituresThunks.list({ branchId, params: listParams }));
  };

  const reloadCoachSalaries = async () => {
    if (!branchId) return;
    setSalaryStatus('loading');
    setSalaryError(null);
    try {
      const { data } = await crmApi(branchId).get('/expenditures/coach-salaries', {
        params: listParams,
      });
      setSalaryRows(data.items ?? []);
      setSalaryTotal({
        actual: (data.items ?? []).reduce((acc, row) => acc + Number(row.actualTotal ?? 0), 0),
        planned: Number(data.total ?? 0),
      });
      setSalaryStatus('idle');
    } catch (e) {
      setSalaryRows([]);
      setSalaryTotal({ actual: 0, planned: 0 });
      setSalaryStatus('error');
      setSalaryError(errMsg(e, 'Не удалось загрузить зарплаты педагогов'));
    }
  };

  useEffect(() => {
    reloadList();
  }, [dispatch, branchId, listParams]);

  useEffect(() => {
    if (activeTab === 'coachSalaries' && canViewCoachSalaries) reloadCoachSalaries();
  }, [activeTab, branchId, canViewCoachSalaries, listParams]);

  useEffect(() => {
    if (activeTab === 'coachSalaries' && !canViewCoachSalaries) {
      setActiveTab('expenses');
    }
  }, [activeTab, canViewCoachSalaries]);

  const total = useMemo(
    () => list.reduce((acc, it) => acc + Number(it.amount ?? 0), 0),
    [list],
  );

  return (
    <section className="expenditures-page">
      <header className="page-header">
        <h1>Расходы</h1>
        <div className="page-header__spacer" />
        <div className="expenditures-page__toolbar">
          <UiDateRangePicker
            from={from}
            to={to}
            onChange={({ from: nextFrom, to: nextTo }) => {
              setFrom(nextFrom);
              setTo(nextTo);
            }}
          />
          <UiButton
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => {
              setFrom(currentMonthStart());
              setTo(today());
            }}
          >
            Текущий месяц
          </UiButton>
        </div>
        {canCreate && (
          <UiButton type="button" onClick={() => setEditing({})}>
            Новый расход
          </UiButton>
        )}
      </header>

      <div className="page-tabs" style={{ marginTop: 16 }}>
        <button
          type="button"
          className={activeTab === 'expenses' ? 'active' : ''}
          onClick={() => setActiveTab('expenses')}
        >
          Список расходов
        </button>
        {canViewCoachSalaries && (
          <button
            type="button"
            className={activeTab === 'coachSalaries' ? 'active' : ''}
            onClick={() => setActiveTab('coachSalaries')}
          >
            Зарплаты педагогов
          </button>
        )}
      </div>

      {editing !== null && (editing.id ? canEdit : canCreate) && (
        <ExpForm
          branchId={branchId}
          item={editing}
          saveStatus={saveStatus}
          saveError={saveError}
          onCancel={() => {
            setEditing(null);
            dispatch(expendituresActions.resetSave(branchId));
          }}
          onSaved={() => {
            setEditing(null);
            reloadList();
          }}
        />
      )}

      {activeTab === 'expenses' ? (
        <div className="form-card expenditures-page__table-card" style={{ marginTop: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Название</th>
                <th>Группа</th>
                <th style={{ textAlign: 'right' }}>Сумма</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((it) => (
                <tr key={it.id}>
                  <td>{it.spentAt ?? '—'}</td>
                  <td>{it.name ?? '—'}</td>
                  <td>{it.groupName ?? it.groupId ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtAmount(it.amount)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {canEdit && (
                      <UiButton variant="ghost" size="sm" type="button" onClick={() => setEditing(it)}>
                        Изменить
                      </UiButton>
                    )}
                    {canDelete && (
                      <UiButton
                        variant="danger"
                        size="sm"
                        type="button"
                        onClick={() => {
                          if (window.confirm('Удалить расход?')) {
                            dispatch(expendituresThunks.remove({ branchId, id: it.id }));
                          }
                        }}
                      >
                        Удалить
                      </UiButton>
                    )}
                  </td>
                </tr>
              ))}
              {!list.length && listStatus !== 'loading' && (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                    Пока нет расходов
                  </td>
                </tr>
              )}
            </tbody>
            {list.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 500 }}>
                    Итого:
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtAmount(total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <div className="form-card expenditures-page__table-card" style={{ marginTop: 16 }}>
          {salaryError ? <div className="form-error">{salaryError}</div> : null}
          <table className="data-table">
            <thead>
              <tr>
                <th>Педагог</th>
                <th style={{ textAlign: 'right' }}>Проведено занятий</th>
                <th style={{ textAlign: 'right' }}>Всего занятий</th>
                <th style={{ textAlign: 'right' }}>Фактическая зарплата</th>
                <th style={{ textAlign: 'right' }}>Планируемая зарплата</th>
              </tr>
            </thead>
            <tbody>
              {salaryRows.map((row) => (
                <tr key={row.managerId}>
                  <td>{row.managerName ?? row.managerId}</td>
                  <td style={{ textAlign: 'right' }}>{row.actualLessonsCount}</td>
                  <td style={{ textAlign: 'right' }}>{row.plannedLessonsCount}</td>
                  <td style={{ textAlign: 'right' }}>{fmtAmount(row.actualTotal)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtAmount(row.plannedTotal)}</td>
                </tr>
              ))}
              {!salaryRows.length && salaryStatus !== 'loading' && (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                    За выбранный период зарплат нет
                  </td>
                </tr>
              )}
              {salaryStatus === 'loading' ? (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                    Загрузка…
                  </td>
                </tr>
              ) : null}
            </tbody>
            {salaryRows.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 500 }}>
                    Итого:
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtAmount(salaryTotal.actual)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtAmount(salaryTotal.planned)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </section>
  );
}

function ExpForm({ branchId, item, saveStatus, saveError, onCancel, onSaved }) {
  const dispatch = useDispatch();
  const isNew = !item.id;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: item.name ?? '',
      amount: item.amount ?? '',
      spentAt: item.spentAt ?? today(),
      groupId: item.groupId ?? '',
    },
  });

  const onSubmit = async (values) => {
    const payload = {
      name: values.name.trim() || null,
      amount: Number(values.amount),
      spentAt: values.spentAt,
      groupId: values.groupId.trim() || null,
    };
    const action = isNew
      ? expendituresThunks.create({ branchId, payload })
      : expendituresThunks.update({ branchId, id: item.id, payload });
    const res = await dispatch(action);
    if (res.meta.requestStatus === 'fulfilled') onSaved();
  };

  return (
    <form className="form-card" onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 16 }}>
      <h3>{isNew ? 'Новый расход' : 'Редактирование'}</h3>
      <div className="form-grid">
        <div className="field">
          <label>Дата</label>
          <UiInput type="date" {...register('spentAt', { required: 'Обязательно' })} />
          {errors.spentAt && <span className="form-error">{errors.spentAt.message}</span>}
        </div>
        <div className="field">
          <label>Сумма</label>
          <UiInput
            type="number"
            step="0.01"
            {...register('amount', { required: 'Обязательно', valueAsNumber: true })}
          />
          {errors.amount && <span className="form-error">{errors.amount.message}</span>}
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Название</label>
          <UiInput {...register('name')} />
        </div>
        <div className="field">
          <label>ID группы (из настроек владельца)</label>
          <UiInput {...register('groupId')} placeholder="Необязательно" />
        </div>
      </div>

      {saveError && <div className="form-error">{saveError}</div>}

      <div className="form-actions">
        <UiButton type="submit" disabled={saveStatus === 'loading'}>
          {saveStatus === 'loading' ? 'Сохранение…' : 'Сохранить'}
        </UiButton>
        <UiButton variant="ghost" type="button" onClick={onCancel}>
          Отмена
        </UiButton>
      </div>
    </form>
  );
}
